import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { toast } from '../../../design-system/components';
import {
  emberApi, apiErrorCode, apiFailureReason, emberIsWhole, newIdempotencyKey, photoRoomOf, photoRoomOfError,
  type ConversationEmber, type ConversationMessage, type ConversationStatus, type EntryType,
  type HelpAskAnswer, type HelpAskOutcome, type AgeCheck,
} from '../api';
import type { MascotFace } from '../../../../../shared/ember-mascot.js';
import { useSession } from '../../auth';
import { track, trackFailed } from '../../../../../shared-web/track';
import { clearSay, readSay } from '../../../../../shared-web/say-handoff';
import { navigate, replaceUrl, sanitizeReturnTo, useRoute } from '../router';
import { subscribeConversation, type LiveConversation, type LiveEvent } from '../live';
import { parseAboutParam } from '../talk-about';
import type { ConversationFeatureCard, ConversationJoinCard, ConversationPrecedentItem, ConversationReadItem, ConversationShareAsk, CrisisLines, GuestDoor, TurnAbout } from '../api';
import type { ShareAnswer } from '../components/KnockCard';
import { PageBack } from '../components/PageBack';
import { ChatsBack } from '../components/ChatsBack';
import { EmberDisc } from '../components/EmberDisc';
import { PresenceAvatar } from '../components/PresenceAvatar';
import { PageHome } from '../components/PageHome';
import { PageLoading } from '../components/PageLoading';
import { CallGlyph } from '../components/CallGlyph';
import { ConversationShell, type PendingPhoto } from '../components/ConversationShell';
import { AvatarSheet } from '../components/AvatarSheet';
import { EmberArrival } from '../components/EmberArrival';
import { EmberReadPage } from '../components/EmberReadPage';
import type { MicUnavailableReason } from '../components/voice-input';
import { InboundRegistrationCTA } from '../components/InboundRegistrationCTA';
import { GuestDoorPanel, GuestInviteCard } from '../components/GuestDoor';
import './Talk.css';
import { RoomThread } from './RoomThread';
import { useChatFrame } from '../chat-frame';
import { chatHereOf, hasChatList, unreadElsewhere } from '../chats';
import { useChatsSnapshot, useEmberSeen } from '../chats-store';
import { notifyKnocksChanged } from '../components/use-knocks';
import { useVisitorConsent } from '../../../../../shared-web/inbound-door';
import { mergeEmbers, mergeTurns } from '../thread-window';
import { clientConfig, useClientConfig } from '../client-config';
import { useStaleReloadHold } from '../use-stale-reload-hold';
import { useKeptAcrossReload } from '../use-kept-across-reload';
import { MinorNotice } from '../components/MinorNotice';

// Photos picked and not yet sent, kept across a reload onto a newer deploy.
const PHOTOS_STORE_PREFIX = 'ember-talk-photos:';

// A guest's Maybe later to the invite (2026-10-27 founder; GuestDoor.tsx): said once in this browser, kept here — the
// door at its count stands either way.
const INVITE_LATER_KEY = 'ember-guest-invite-later';
function readInviteLater(): boolean {
  try { return window.localStorage.getItem(INVITE_LATER_KEY) === '1'; } catch { return false; }
}
function writeInviteLater(): void {
  try { window.localStorage.setItem(INVITE_LATER_KEY, '1'); } catch { /* the card goes for this page either way */ }
}

export interface TalkPageProps {
  search: URLSearchParams;
}

const ENTRY_TYPES: EntryType[] = ['home', 'campaign', 'node'];

/**
 * The conversation this page entry should resume, '' for a fresh telling.
 *
 * Only a link that names one (a Result page's Keep telling, an old
 * needs-more-context link through the chooser): this page never writes an id
 * into its own URL (2026-09-30 拍板). Until then boot() stamped the created
 * conversation's id onto the URL, and the in-app browsers that report every
 * URL to their platform handed it to link scanners, which replayed it from
 * fresh browsers for weeks (prod, 30 days: 65 such browsers, each refused with
 * not_found). The id was never what brought a browser back to its conversation
 * — the edge answers POST /api/conversations with the open one of whoever the
 * cookie or account says is asking (one person, one conversation, §4.5) — so
 * a reload or a remount resumes without it, and a copied URL carries nothing.
 *
 * The live URL, never the `search` prop: replaceUrl announces no navigation, so
 * the router's snapshot lags behind what boot() takes off the URL, and reading
 * the prop re-resumed an id the server had just refused — a boot() ↔ not_found
 * loop at ~2.5 requests/s until the person left (prod 2026-09-03, 90 rows from
 * one Android in-app browser).
 */
function resumeConversationId(): string {
  return new URLSearchParams(window.location.search).get('conversation_id') || '';
}

/** Take the resumed telling off the URL, silently — the next boot is a fresh arrival. */
function forgetConversationInUrl(): void {
  const qs = new URLSearchParams(window.location.search);
  qs.delete('conversation_id');
  const query = qs.toString();
  replaceUrl(`${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`);
}

// How long a turn may sit on the wire before this page gives up on it. The
// reply is generated inside the request, so it is normally back in a few
// seconds and rarely past twenty; sixty is far beyond a slow reply and far
// short of forever, which is what an instance switching under the request
// used to mean for `sending`.
const SEND_TIMEOUT_MS = 60_000;
// After a timed-out send, one quick look at the transcript decides whether the
// turn landed anyway (then it stays) or never arrived (then the text comes
// back for a retry). Bounded, so a network that is truly gone cannot hold the
// composer a second time.
const SEND_RESYNC_RACE_MS = 5_000;
// A send answered 409 in_progress (idempotency.js, row mode): this turn's
// first copy is still running on the edge — the retry after a dropped
// connection met it. Nothing is wrong; the reply is on its way. Wait for it
// quietly, looking every few seconds, for about as long as a slow turn takes.
const IN_PROGRESS_POLL_MS = 2_000;
const IN_PROGRESS_WAIT_MS = 60_000;

/**
 * Four ways to begin (home arrivals only — a telling that arrived from a
 * question has the question). A tap SENDS the line as the person's first
 * turn, the way a ChatGPT conversation starter does (拍板 2026-09-03: aligned
 * with GPT, no fill-then-confirm), so each must read as a complete first
 * sentence in their voice. Each names a DOMAIN of life, not a state of mind
 * (拍板 2026-09-14): a person who taps one has already picked their subject,
 * so Ember's first question has somewhere to point and the real telling
 * arrives as the second turn. 09-04→09-10 the three domain chips (work /
 * family / a moment) carried 49 taps to 27 tellings of 3+ turns and 24
 * Embers; 09-12→09-14 the homepage's four state-of-mind starters ("I keep
 * going back and forth about something." …), copied here for consistency and
 * withdrawn from the homepage the same night, carried 6 taps to none past
 * turn 2 — a state names nothing, so Ember could only ask "what is it?" and
 * the person faced the same blank they tapped to avoid. The partner line is
 * new: the lights people stand at most (stopped replying, cheating, ended
 * things, divorce) are between partners, and "family" never read as that.
 */
const TALK_STARTERS = [
  'Something between me and my partner.',
  'Something with my family.',
  'Something at work I can’t let go of.',
  'A moment I keep replaying.',
];

const TALK_PREVIEW_MESSAGES: ConversationMessage[] = [
  {
    role: 'assistant',
    content: 'What has been on your mind lately? Tell me about something you actually lived through — start anywhere.',
    turn_index: 0,
  },
  {
    role: 'user',
    content: 'I keep saying I am fine at work, but lately even small things feel heavier than they should.',
    turn_index: 1,
  },
  {
    role: 'assistant',
    content: 'When did you first notice that the weight had changed — was there a particular moment, or did it build quietly?',
    turn_index: 2,
  },
  {
    role: 'user',
    content: 'It built quietly. Last Friday I sat in my car for twenty minutes before going inside because I could not make myself put on the “everything is handled” version of me again.',
    turn_index: 3,
  },
  {
    role: 'assistant',
    content: 'Stay with that Friday for a moment. What were you afraid would happen if you walked in without putting that version of yourself on?',
    turn_index: 4,
  },
];

/**
 * The talk page's header, on its own so a room's page (/rooms/:id) wears the
 * same one (2026-10-06, jam: the room changed headers the moment it closed —
 * the thread under this header, then the site nav over the room's page). It is
 * the chat's bar (2026-10-20 skins). A signed-in person goes back to the Chats
 * list from any conversation (2026-10-21 Riley; until then only someone with
 * something past Ember on it), the count of what is unread in the others on
 * the way (ChatsBack); beside the list (two panes) there is no way back to
 * make, and the list's Home is the one. A guest keeps the page-level Back.
 */
export function TalkPageHeader({ returnTo, to, action }: {
  /** The route's explicit returnTo, when it carries one. */
  returnTo?: string;
  /** The page's own safe landing when nothing usable is behind this entry; '/' when unset. */
  to?: string;
  /** Optional quiet page-level utility on the header's far side (frontend-ui-standard §4 family 1). */
  action?: ReactNode;
}): ReactElement {
  const loc = useRoute();
  const { status } = useSession();
  const { split } = useChatFrame();
  const { data } = useChatsSnapshot();
  const here = chatHereOf(loc.path, loc.search);
  const listed = here !== null && hasChatList(status);
  // Which conversation this is, in the bar (2026-10-20, with the list): Ember's face and name, or a room's people
  // and its name — the row the list shows for it.
  const room = here?.kind === 'room' ? data?.chats.find((c) => c.room_id === here.roomId) ?? null : null;
  const who = !listed ? null : here?.kind === 'ember' ? (
    <span className="talk-header-who"><EmberDisc /><span className="talk-header-name">Ember</span></span>
  ) : room ? (
    <span className="talk-header-who">
      {room.people.length > 0 && (
        <span className="talk-header-stack" aria-hidden>
          {room.people.slice(0, 3).map((p) => <PresenceAvatar key={p.name} avatarKey={p.avatar} size={22} />)}
        </span>
      )}
      <span className="talk-header-name" dir="auto">{room.name}</span>
    </span>
  ) : null;
  return (
    <div className="talk-bar">
      <header className="talk-page-header">
        {/* Both ways out sit together on the left (拍板 2026-09-10): the
            conversation is server-persisted and never ends (2026-09-12), so
            leaving is never data loss — it is just leaving; this page is often
            reached several hops deep. The right cluster keeps the voice door. */}
        <div className="talk-header-nav">
          {!listed ? <PageBack returnTo={returnTo} to={to} className="talk-back" />
            : split ? null : <ChatsBack count={unreadElsewhere(data, here)} className="talk-back" />}
          {/* Beside the list, its own Home is the one. */}
          {!(listed && split) && <PageHome />}
        </div>
        {/* The middle of the row: empty without a list (Ember itself sits
            beside its turns in the thread since 2026-09-13); with one, the
            conversation's name. */}
        <div className="talk-header-gap">{who}</div>
        <div className="ember-toolbar-actions talk-header-actions">
          {action}
        </div>
      </header>
    </div>
  );
}

function TalkFrame({ children, returnTo, action }: {
  children: ReactNode;
  returnTo: string;
  /** Optional quiet page-level utility on the header's far side (frontend-ui-standard §4 family 1). */
  action?: ReactNode;
}): ReactElement {
  const { skin } = useChatFrame();
  return (
    <div className="talk-page" data-eh-viewport-locked data-chat-skin={skin}>
      <TalkPageHeader returnTo={returnTo} action={action} />
      <div className="talk-stage">{children}</div>
    </div>
  );
}

/**
 * S05 — Unified Conversation (§4.5). Every entry (home CTA, a light on the
 * tree, campaign link) lands here with only an entry_context; the edge
 * resolves it to the person's one open conversation (or opens it, the AI
 * speaking first). Nothing here ends the conversation (2026-09-12): the person
 * talks, and when they have been quiet a while the edge writes what was told
 * into an Ember, which appears in this thread as a paper card. The page polls
 * while open, so a tab left open sees it arrive.
 */

export function TalkPage({ search }: TalkPageProps): ReactElement {
  // The admin-set client constants (2026-10-19 Riley, client-config.ts): photos per message / conversation,
  // the poll interval and the turns a read brings — the edge's once /api/app/config has answered.
  const cfg = useClientConfig();
  const returnTo = sanitizeReturnTo(search.get('returnTo'), '');
  const { status: sessionStatus, refresh: refreshSession } = useSession();
  // No letter strip under the header (2026-10-20 founder): what waits on this person reaches the thread itself — a
  // reader's note or reply and a room's invitation as a notice line, the share ask in Ember's own words — and also
  // stands at the tree's feet, on the Ember's card in the notebook and, for a room, in the Chats list. The strip said
  // it a second time, and the share ask ahead of the moment Ember picks to ask it.
  // The browser's standing 18+ / Terms record (the same one every registration
  // door reads): a visitor who ticked the boxes once is not asked again for
  // every new telling. The server keeps the authoritative copy and still
  // refuses (`consent_required`) when the record is missing or stale.
  const consentRecord = useVisitorConsent();
  // Dev uses the deployed conversation service by default. Mock conversations
  // are opt-in preview states so an ordinary /talk?mode=chat exercises the
  // same API path as production instead of silently becoming a demo.
  // A room's thread in this frame when the URL names one (?room=; §4.21) —
  // reached from the Chats list, a banner, or a room's link (2026-10-20).
  const roomInFront = search.get('room') || '';
  const preview = search.get('preview');
  const previewMock = import.meta.env.DEV && preview === 'mock';
  const [conv, setConv] = useState<{
    id: string; status: ConversationStatus; entryType: string; entryId: string | null;
    /** The settlement cursor (§6.2); the photo cap counts the turns after it. */
    writtenThroughTurn: number | null;
    /** When the crisis resources were last placed here (2026-09-17); the shell draws the card. */
    crisisReferredAt: string | null;
    /** When the lines for a risk of hurting someone else were last placed here (2026-10-12). */
    harmReferredAt: string | null;
    /** The two cards' words, in the person's country's lines (2026-10-29); null until either was placed. */
    crisisLines: CrisisLines | null;
    /** The age check standing on this person (edge age-check.js, 2026-10-27): the shell draws its question after the turns. */
    ageCheck: AgeCheck | null;
  } | null>(null);
  // Closed as under 18 (edge age-check.js, 2026-11-02 founder): they answered the age check so here, or the browser was
  // stopped (edge `not_served` — on opening, an old link, a turn). The page is the minor notice from then on.
  const [notServed, setNotServed] = useState(false);
  // A legacy conversation that already finalized (a Result link opened in this
  // page's URL from before 2026-09-12, or a voice telling's row): its home is
  // the Result page, and this page has nothing to add to it.
  const legacyFinalized = (status: ConversationStatus): boolean => status === 'finalizing' || status === 'finalized';
  // The turns this page holds: a window on the conversation (thread-window.ts,
  // 2026-09-25) — the newest THREAD_WINDOW at boot, older ones as the reader
  // scrolls up, newer ones as the poll brings them.
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  // Whether turns older than messages[0] exist on the edge, and that turn's
  // index — the `before` of the next reach back. `loading` while one is on the wire.
  const [history, setHistory] = useState<{ hasMore: boolean; oldest: number | null; loading: boolean }>({ hasMore: false, oldest: null, loading: false });
  // The Embers written from this conversation, as the edge knows them.
  const [embers, setEmbers] = useState<ConversationEmber[]>([]);
  // Sharing (§4.9, 2026-09-27): the ask waiting on this person, as the last
  // read saw it (the card stands in the thread while it waits; the next poll
  // after an answer drops it), and the Embers someone let them read here.
  const [shareAsk, setShareAsk] = useState<ConversationShareAsk | null>(null);
  const [reads, setReads] = useState<ConversationReadItem[]>([]);
  const [precedents, setPrecedents] = useState<ConversationPrecedentItem[]>([]);
  const [joinCards, setJoinCards] = useState<ConversationJoinCard[]>([]);
  const [featureCards, setFeatureCards] = useState<ConversationFeatureCard[]>([]);
  // A guest's door (2026-10-27 founder; GuestDoor.tsx): what the edge last said of it, the reply the invite first came
  // with, and whether this browser said Maybe later to the invite.
  const [guestDoor, setGuestDoor] = useState<GuestDoor | null>(null);
  const [inviteAfter, setInviteAfter] = useState<number | null>(null);
  const [inviteLater, setInviteLater] = useState<boolean>(readInviteLater);
  // The three moments (2026-10-20, edge moments.js): the openers of the silent start's line when it is this
  // conversation's latest word (they stand in the starters' place), and the silent start's clock when the opening
  // read asked for one. `composerTouched`: anything begun in the composer — a character, the mic, the photo picker.
  const [openingStarters, setOpeningStarters] = useState<string[] | null>(null);
  const [silentStart, setSilentStart] = useState<{ arm: 'on' | 'off'; after_ms: number } | null>(null);
  const [composerTouched, setComposerTouched] = useState(false);
  // The arrival (2026-09-12 拍板, §4.5 / §4.17). An Ember is drawn in the
  // thread only once it is whole (text, cover, music) AND has been presented:
  // the first time the whole Ember is in front of its owner — back after a
  // quiet hour, or just off a call — the ceremony plays over the chat and
  // lands it as its card, and the edge is told. `presented` is the ids this
  // page has landed (the edge's stamp reaches the payload on the next poll);
  // `arrival` is the one playing now and how far it is; `reading` is the
  // Ember open as a page over the thread.
  const [presented, setPresented] = useState<ReadonlySet<string>>(() => new Set());
  const [arrival, setArrival] = useState<{ ids: string[]; stage: 'ceremony' | 'landed' } | null>(null);
  const [reading, setReading] = useState<ConversationEmber | null>(null);
  // Opened by the URL rather than by a tap: the sheet is drawn already in
  // place (see EmberReadPage `restored`).
  const [readingRestored, setReadingRestored] = useState(false);
  /**
   * The open Ember rides the URL (`?ember=<id>`, 2026-09-24). The sheet has
   * doors that leave the page — the light it stands at, the transcript — and
   * Back from those has to land on the Ember again, not on the bare thread.
   * `replaceUrl`, not a push: opening the sheet is not a step of its own, it
   * rewrites the entry the person is standing on, so Back from the light's
   * page returns to exactly this — the thread with the Ember open.
   * The param is consumed once per mount (`wantEmber`): closing the sheet by
   * hand takes it off the URL, and nothing here puts it back.
   */
  const openReading = (e: ConversationEmber | null): void => {
    setReading(e);
    setReadingRestored(false);
    const qs = new URLSearchParams(window.location.search);
    if (e) qs.set('ember', e.id); else qs.delete('ember');
    const q = qs.toString();
    replaceUrl(`${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash || ''}`);
  };
  const [wantEmber, setWantEmber] = useState(() => search.get('ember') || '');
  // "Was this any help?" (2026-09-15 拍板, §4.5): the row open over the
  // composer right now — the edge's ask id, so its end can be reported.
  const [helpAsk, setHelpAsk] = useState<{ id: string } | null>(null);
  const shownEmbers = embers.filter((e) => e.presented_at || presented.has(e.id) || arrival?.ids.includes(e.id));
  // Landing on `?ember=<id>` (a Back from the light's page or the transcript,
  // or a reload): open it as soon as the thread's Embers are here. The ask is
  // consumed either way — an id that is not in this thread any more (deleted,
  // or another browser's) must not leave the page waiting for ever. The
  // thread is a window (2026-09-25): an Ember placed further back than the
  // newest turns is not here yet, and the ask lands on the thread instead.
  useEffect(() => {
    if (!wantEmber || embers.length === 0) return;
    const found = embers.find((e) => e.id === wantEmber);
    setWantEmber('');
    if (!found) return;
    setReadingRestored(true);
    setReading(found);
  }, [wantEmber, embers]);
  // The question a Request arrival is answering, for the strip under the
  // header. '' until loaded, and for every other entry.
  const [sending, setSending] = useState(false);
  // The person's turn whose reply failed after the words were stored
  // (2026-09-21): the thread offers Try again under it, and Ember answers
  // what is there — no copy of the turn is sent. Null: nothing failed.
  const [failedTurn, setFailedTurn] = useState<number | null>(null);
  // The person's turn whose reply they stopped (2026-10-17 jam: A, "Let Ember
  // reply"): the words stay in the thread with no answer, and until now
  // nothing on screen said why — after an accidental Stop it read as Ember
  // not answering. The same line as a failed reply offers the answer. Null:
  // nothing stopped, or the person has moved on.
  const [stoppedTurn, setStoppedTurn] = useState<number | null>(null);
  // The edge's last read said no reply is being made for this conversation
  // (2026-10-17 prod walkthrough: a Stop, a failure, or a text the edge never
  // answered, read again after a reload, left the person's words with no way
  // to an answer). With their words last, the thread offers Let Ember reply
  // alone — the page cannot know why nothing answered (jam 拍板).
  const [replyIdle, setReplyIdle] = useState(false);
  // The person's picture beside their turns (2026-09-13), as the edge knows
  // it; the avatar sheet changes it in place.
  const [viewerAvatar, setViewerAvatar] = useState('');
  const [pickingAvatar, setPickingAvatar] = useState(false);
  const [initFailed, setInitFailed] = useState(false);
  // The guest free-telling budget is spent (`registration_required`): the next
  // telling starts by registering — one prefilled text (§4.6.2), never /auth.
  // `sign_in` (2026-10-01 拍板, `sign_in_required`): the same door for a
  // browser bound to an account that signed out — it reads "Sign in", not
  // "you have used your free conversations".
  const [needsRegistration, setNeedsRegistration] = useState<'register' | 'sign_in' | null>(null);
  // Photos picked but not yet sent. Each is already stored and moderated by
  // the time `uploading` clears — the tray shows what the person is about to
  // hand over, not a queue of things that might still be refused.
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  // A newer deploy reloads this page (stale-bundle-reload) back into this
  // conversation (?conversation_id); a turn on its way is let land first. The
  // photos picked and not yet sent are kept across it (each is already hosted
  // — only one still uploading is lost); the composer keeps its own words.
  useStaleReloadHold(sending);
  const readyPhotos = photos.filter((p) => !p.uploading && p.mediaId);
  useKeptAcrossReload(
    conv?.id ? `${PHOTOS_STORE_PREFIX}${conv.id}` : null,
    readyPhotos.length ? JSON.stringify(readyPhotos) : '',
    (kept) => {
      const saved = JSON.parse(kept) as PendingPhoto[];
      if (Array.isArray(saved) && saved.length) setPhotos(saved.slice(0, clientConfig().photos_per_conversation));
    },
  );
  // The + button is offered to signed-in tellers only (拍板). A guest tells in
  // words; the button simply is not there rather than failing on tap.

  const aliveRef = useRef(true);
  // The turns as last rendered, for callbacks that must read them without
  // re-arming on every change (the poll, the reach back).
  const messagesRef = useRef<ConversationMessage[]>(messages);
  messagesRef.current = messages;
  const historyRef = useRef(history);
  historyRef.current = history;
  // Which telling this page is showing, readable from a callback that began
  // while a different one was open. A turn's post-Stop resync re-reads the id
  // it was sent with, so once "New" has put that telling down its answer must
  // not land — it would draw the old transcript into the new conversation.
  const shownConvRef = useRef<string | null>(null);
  // The status poll's failure is recorded once per visit (see the poll).
  const pollFailedSent = useRef(false);
  // The count of letters waiting on this person, as the last poll saw it
  // (viewer.letters_pending, 2026-09-18): when it moves, the knocks are
  // re-read and the door line appears without a reload.
  const lettersPending = useRef<number | null>(null);
  // A reopen (see reopen) is pending until the conversation it produced has
  // been read back once: a second refusal before then means this browser
  // cannot hold on to a conversation at all, and the page stops asking.
  const reopenedRef = useRef(false);
  const bootedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  // Each turn's own key (2026-10-21 Riley; api.ts sendMessage turnKey): minted
  // when the person sends, kept by the turn's index so Try again under that
  // turn carries the same one, and with the words when they come back to the
  // field so the next tap sends the same turn, not a second copy of it. The
  // edge answers the same key with the same turn — nothing written twice, no
  // second reply (prod 10-20: Try again while the first reply was still being
  // written answered one turn twice).
  const turnKeys = useRef(new Map<number, string>());
  // The conversation's channel (phase 3, live.ts): joined while this page shows the conversation; `turn` events
  // pull what landed, `reply` events feed the bubble of the send they belong to (by turn_key, deduped by seq)
  const liveConv = useRef<LiveConversation | null>(null);
  const replyHandlers = useRef(new Map<string, { seq: number; delta: (t: string) => void; face: (f: string) => void }>());
  const pullRef = useRef<(() => Promise<void>) | null>(null);
  const pullMissed = useRef(false);
  const restoredKey = useRef<{ text: string; key: string } | null>(null);
  // Stop (2026-09-03): the user turn whose reply is in flight, and the stop the
  // person asked for it. The edge is told FIRST (stopReply cancels the brain
  // call, or deletes a reply that just landed) and only then is our request
  // dropped — the abort alone never told the edge anything, so the reply came
  // back on re-sync as if nothing had been pressed.
  const inFlightTurnRef = useRef<number | null>(null);
  const stopRef = useRef<{ turnIndex: number; done: Promise<unknown> } | null>(null);

  // Any object URL still held by an in-flight upload dies with the page.
  const photosRef = useRef<PendingPhoto[]>([]);
  photosRef.current = photos;
  useEffect(() => () => {
    for (const p of photosRef.current) {
      if (p.previewUrl.startsWith('blob:')) URL.revokeObjectURL(p.previewUrl);
    }
  }, []);

  useEffect(() => { shownConvRef.current = conv?.id ?? null; }, [conv?.id]);

  // The stretch's photos and the cap, as the edge last said them (2026-10-19 Riley): on the conversation read,
  // its poll, every upload and refusal, and the photos read the + makes. The page used to count the photos in
  // the turns it had loaded — its newest 40 — and offered room in a long stretch that the upload then refused.
  const [photoRoom, setPhotoRoom] = useState<{ used: number; limit: number } | null>(null);
  const photoRoomRef = useRef(photoRoom);
  photoRoomRef.current = photoRoom;
  const takeRoom = (body: unknown): void => {
    const room = photoRoomOf(body);
    if (room && aliveRef.current) setPhotoRoom(room);
  };
  // The photos read the + started, which the picked batch is trimmed by.
  const roomReadRef = useRef<Promise<{ used: number; limit: number } | null> | null>(null);
  // Photos the cap refused since the last turn (2026-10-19 Riley): the next turn carries the count, and the edge
  // keeps it as a line of that turn, so Ember knows they did not get in.
  const notAddedRef = useRef(0);
  // Tray photos taken out while still uploading: when the upload lands, the edge is told to drop it too.
  const removedKeysRef = useRef<Set<string>>(new Set());

  // The homepage composer (2026-09-12, HeroComposer): the sentence typed under
  // the tree is sent as this person's next turn the moment the conversation is
  // open. The URL carries only its handle; the words wait in this browser
  // (say-handoff.ts, 2026-09-30) and are forgotten, and the handle taken off
  // the URL, once said — so a reload never sends them twice.
  const sayHandleRef = useRef<string>(search.get('say') || '');
  const sayRef = useRef<string>(readSay(sayHandleRef.current));
  // "Have thoughts about this?" (2026-09-24, talk-about.ts): a first sentence
  // for the field and what travels with that turn — read once off the URL and
  // taken off it, so a reload does not put the sentence back.
  // The words a Stop took back before the edge had them (dev e2e 2026-10-19: on a slow link, Stop ~1.5 s after Send
  // aborted the request on the wire, the transcript never got the turn, and the sentence vanished from the screen with
  // nothing said). They go back into the field, as a failed send's do; the sheet reads this like the door's draft.
  const [restoredDraft, setRestoredDraft] = useState<{ text: string; about: TurnAbout | null } | null>(null);
  // A handoff's words for the field travel by handle (`draft_say`, the yard's starred line since 2026-11-02), not on the URL.
  const [draft] = useState<{ text: string; about: TurnAbout | null } | null>(() => {
    const text = (search.get('draft') || readSay(search.get('draft_say') || '')).slice(0, 2000);
    if (!text) return null;
    return { text, about: parseAboutParam(search.get('about'), search.get('about_label')) };
  });
  useEffect(() => {
    if (!draft) return;
    const qs = new URLSearchParams(window.location.search);
    clearSay(qs.get('draft_say') || '');
    qs.delete('draft'); qs.delete('draft_say'); qs.delete('about'); qs.delete('about_label');
    replaceUrl(`${window.location.pathname}${qs.toString() ? `?${qs.toString()}` : ''}${window.location.hash || ''}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!conv || conv.status !== 'active' || sending || !sayRef.current) return;
    const sentence = sayRef.current;
    sayRef.current = '';
    clearSay(sayHandleRef.current);
    sayHandleRef.current = '';
    const qs = new URLSearchParams(window.location.search);
    qs.delete('say');
    replaceUrl(`${window.location.pathname}?${qs.toString()}${window.location.hash || ''}`);
    void handleSend(sentence);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv?.id, conv?.status]);

  // The telling door (TellingDoors, §4.5): a signed-out browser that has not
  // yet ticked 18+ and the Terms is sent back to the door that asks them —
  // the same door every entrance shows, never a page of its own here
  // (2026-09-08 拍板). Entry context and the way back ride along.
  const doorUrl = (): string => {
    const qs = new URLSearchParams();
    const t = search.get('entry_type');
    const id = search.get('entry_id');
    if (t) qs.set('entry_type', t);
    if (id) qs.set('entry_id', id);
    if (returnTo) qs.set('returnTo', returnTo);
    return `/talk${qs.toString() ? `?${qs.toString()}` : ''}`;
  };

  // One conversation per page entry. The component is keyed by the query
  // string in EmberApp, so a different entry context always remounts.
  useEffect(() => {
    aliveRef.current = true;
    if (bootedRef.current) return () => { aliveRef.current = false; };
    if (sessionStatus === 'loading') return () => { aliveRef.current = false; };
    // A fresh anonymous chat waits for the browser's consent record: ticked
    // (on the door, which wrote it) → open the telling straight away; never
    // ticked → back to the door, without creating a doomed request just to
    // learn that from the edge.
    if (sessionStatus === 'anonymous' && !resumeConversationId()) {
      if (!consentRecord.known) return () => { aliveRef.current = false; };
      bootedRef.current = true;
      if (consentRecord.consented) void boot();
      else navigate(doorUrl(), { replace: true });
    } else {
      bootedRef.current = true;
      void boot();
    }
    return () => {
      aliveRef.current = false;
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStatus, consentRecord.known, consentRecord.consented]);

  // Closing the tab is reported as a plain fact of what the browser did
  // (`client_ended_at`, diagnostic). It does not end anything: the
  // conversation stays open, and the idle clock alone decides when what was
  // told gets written (2026-08-24; conversation-settlement-worker.js).
  useEffect(() => {
    const id = conv?.id;
    if (!id || conv.status !== 'active') return undefined;
    if (!history.hasMore && !messages.some((m) => m.role === 'user')) return undefined;
    const report = (): void => {
      const body = JSON.stringify({ end_reason: 'page_hidden' });
      const url = `/api/conversations/${encodeURIComponent(id)}/client-ended`;
      try {
        if (navigator.sendBeacon?.(url, body)) return;
      } catch {
        // fall through to fetch
      }
      try {
        void fetch(url, { method: 'POST', body, keepalive: true, credentials: 'same-origin' });
      } catch {
        // Nothing to do: the idle timeout is the backstop for exactly this.
      }
    };
    // iOS Safari often never fires pagehide (see shared-web/page-engagement),
    // so hidden is the last reliable moment there.
    const onHidden = (): void => { if (document.visibilityState === 'hidden') report(); };
    window.addEventListener('pagehide', report);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('pagehide', report);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [conv?.id, conv?.status, messages, history.hasMore]);

  // SMS and web chat write the same conversation, so while this page is open a
  // turn can land from the person's own phone — and, after a quiet hour, the
  // Ember the edge wrote from what was told. Poll the transcript while the
  // conversation is open and the tab is visible. Sends pause it — the send
  // response already carries both new turns.
  useEffect(() => {
    const id = conv?.id;
    const status = conv?.status;
    if (!id || !status || previewMock) return undefined;
    const open = status === 'active' || status === 'needs_more_context' || status === 'failed';
    if (!open || sending) return undefined;
    // `cancelled` covers the fetch still in flight when this effect tears down
    // (a send just started): its stale snapshot must not overwrite the
    // optimistic turn the send is holding.
    let cancelled = false;
    // Since 2026-10-21 (phase 3) no clock: the channel's `turn` says when to pull, the tab's return does, and a
    // `turn` that arrived while a send held this off is pulled once the send is over
    const pull = async (): Promise<void> => {
      if (document.visibilityState === 'hidden') return;
      {
        try {
          // Only what came after the last turn here (thread-window.ts): a
          // conversation that never ends is not re-read whole. The pull is
          // held while a send is on the wire, so the last turn held is the
          // edge's, never an optimistic one.
          const last = messagesRef.current.length > 0 ? messagesRef.current[messagesRef.current.length - 1].turn_index : -1;
          const fresh = await emberApi.getConversation(id, last >= 0 ? { after: last } : { limit: clientConfig().thread_window });
          if (cancelled || !aliveRef.current) return;
          reopenedRef.current = false;
          setMessages((m) => mergeTurns(m, fresh.messages));
          takeRoom(fresh);
          setReplyIdle(fresh.reply_in_flight === false);
          if (last < 0) setHistory((h) => ({ ...h, hasMore: fresh.page.has_more, oldest: fresh.page.oldest_turn_index }));
          setEmbers((e) => mergeEmbers(e, fresh.embers ?? []));
          setShareAsk(fresh.viewer?.share_ask ?? null);
          if (fresh.reads) setReads(fresh.reads);
          if (fresh.precedents) setPrecedents(fresh.precedents);
          if (fresh.join_cards) setJoinCards(fresh.join_cards);
          if (fresh.feature_cards) setFeatureCards(fresh.feature_cards);
          const pending = fresh.viewer?.letters_pending;
          if (typeof pending === 'number' && pending !== lettersPending.current) {
            if (lettersPending.current !== null) notifyKnocksChanged();
            lettersPending.current = pending;
          }
          if (legacyFinalized(fresh.conversation.status)) {
            navigate(`/result/${id}`, { replace: true });
          } else {
            // Status and the settlement cursor both move on the server while
            // this page is open (an Ember written = the cursor advanced).
            const cursor = fresh.conversation.written_through_turn ?? null;
            const crisis = fresh.conversation.crisis_referred_at ?? null;
            const harm = fresh.conversation.harm_referred_at ?? null;
            const lines = fresh.crisis_lines ?? null;
            const age = fresh.age_check ?? null;
            setConv((c) => (c && (c.status !== fresh.conversation.status || c.writtenThroughTurn !== cursor || c.crisisReferredAt !== crisis || c.harmReferredAt !== harm || c.crisisLines?.self_harm !== lines?.self_harm || Boolean(c.ageCheck) !== Boolean(age))
              ? { ...c, status: fresh.conversation.status, writtenThroughTurn: cursor, crisisReferredAt: crisis, harmReferredAt: harm, crisisLines: lines, ageCheck: age } : c));
          }
        } catch (err) {
          if (cancelled || !aliveRef.current) return;
          // `not_found` is not transient and no tick will talk it round: the
          // id this page holds no longer names this browser's conversation.
          // Most often the browser just signed in and the fold moved this
          // guest thread into the account's one conversation (prod
          // 2026-09-16, a second browser on the same phone); the poll that
          // carried the new session is the request that folded it. Left as
          // a "transient" failure the poll asked again every 5s for as long
          // as the page stayed open (dev 2026-09-06, 13 in a row).
          if (apiErrorCode(err) === 'not_found') {
            trackFailed('conversation_open', 'not_found', { conversation_id: id });
            reopen();
            return;
          }
          // Transient read failure: the next pull retries, and every action on
          // this page still carries its own error handling. Recorded once per
          // visit — this pull is what brings a phone turn or the written Ember
          // into view, so a dead one is invisible otherwise.
          if (!pollFailedSent.current) {
            pollFailedSent.current = true;
            trackFailed('conversation_open', 'poll_failed', { conversation_id: id, error_detail: apiFailureReason(err) });
          }
        }
      }
    };
    pullRef.current = pull;
    if (pullMissed.current) { pullMissed.current = false; void pull(); }
    const onVisible = (): void => { if (document.visibilityState === 'visible') void pull(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      pullRef.current = null;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [conv?.id, conv?.status, sending, previewMock]);

  // The conversation's channel: joined while the page shows it (guests too)
  useEffect(() => {
    const id = conv?.id;
    if (!id || previewMock) return undefined;
    const sub = subscribeConversation(id, {
      onEvent: (ev: LiveEvent) => {
        const what = Array.isArray(ev?.what) ? ev.what : [];
        if (what.includes('turn')) {
          if (pullRef.current) void pullRef.current(); else pullMissed.current = true;
        }
        if (what.includes('reply')) {
          const e = ev as LiveEvent & { turn_key?: string; seq?: number; text?: string; face?: string; done?: boolean };
          const h = typeof e.turn_key === 'string' ? replyHandlers.current.get(e.turn_key) : undefined;
          if (!h) return;
          if (typeof e.seq === 'number') { if (e.seq <= h.seq) return; h.seq = e.seq; }
          if (typeof e.face === 'string' && e.face) h.face(e.face);
          if (typeof e.text === 'string' && e.text) h.delta(e.text);
        }
      },
      onSubscribed: (recovered) => { if (!recovered) { if (pullRef.current) void pullRef.current(); else pullMissed.current = true; } },
    });
    liveConv.current = sub;
    return () => { liveConv.current = null; sub.close(); };
  }, [conv?.id, previewMock]);
  // A room's card under a reply says who is in and whether it is open (2026-10-31 founder: a page follows the one change
  // signal): while one is in the thread on screen, every read of their Chats pulls the conversation again, as a turn does.
  const { data: chats } = useChatsSnapshot();
  useEffect(() => {
    if (chats && !roomInFront && featureCards.some((c) => c.what === 'room')) void pullRef.current?.();
  }, [chats]);

  /**
   * The silent start (2026-10-18 拍板; experiment moments_nudge, web only): a home conversation nobody has said
   * anything in, on screen for `after_ms` (a hidden tab's time does not count) with nothing begun in the composer.
   * Both arms record that moment (talk_silent_45s, the denominator); in `on` the page then asks for Ember's one more
   * line, which lands as the next turn with its three first sentences in the starters' place. Once per page; a
   * character, the mic, the photo picker or a send ends it; the edge reads its gate again before it says anything.
   */
  const silentRef = useRef({ conversationId: '', shownMs: 0, since: 0, done: false });
  useEffect(() => {
    const id = conv?.id;
    if (!id || !silentStart || previewMock || composerTouched || sending) return undefined;
    if (history.hasMore || messages.some((m) => m.role === 'user')) return undefined;
    const s = silentRef.current;
    if (s.conversationId !== id) { s.conversationId = id; s.shownMs = 0; s.since = 0; s.done = false; }
    if (s.done) return undefined;
    let timer = 0;
    const fire = (): void => {
      s.since = 0;
      s.shownMs = silentStart.after_ms;
      s.done = true;
      const tMs = Math.round(performance.now());
      track('ui.seen', { name: 'talk_silent_45s', state: silentStart.arm, conversation_id: id, t_ms: tMs });
      if (silentStart.arm !== 'on') return;
      void emberApi.nudge(id, tMs)
        .then((r) => {
          if (!aliveRef.current) return;
          setMessages((m) => mergeTurns(m, [r.assistant_turn]));
          setOpeningStarters(r.opening_starters);
        })
        .catch((err: unknown) => {
          // `not_ready` is the ordinary no: someone typed, or another device was first.
          if (apiErrorCode(err) !== 'not_ready') trackFailed('talk_nudge', 'request_failed', { conversation_id: id, error_detail: apiFailureReason(err) });
        });
    };
    const start = (): void => {
      if (document.visibilityState === 'hidden' || s.since) return;
      s.since = performance.now();
      timer = window.setTimeout(fire, Math.max(0, silentStart.after_ms - s.shownMs));
    };
    const pause = (): void => {
      window.clearTimeout(timer);
      if (s.since) { s.shownMs += performance.now() - s.since; s.since = 0; }
    };
    const onVisibility = (): void => { if (document.visibilityState === 'hidden') pause(); else start(); };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { pause(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [conv?.id, silentStart, composerTouched, sending, messages, history.hasMore, previewMock]);

  /**
   * Open this browser's conversation: the one the URL names when the edge
   * agrees it is this browser's, else whatever POST /api/conversations
   * answers — the person's open conversation (one person, one conversation,
   * §4.5), or a fresh one when they have none. `arrive` is the page's first
   * opening: the entry the URL carries (a light on the tree, a Request) is
   * arrived through, so Ember speaks to where they came from. A reopen
   * (below) has already arrived and asks for the conversation plain.
   *
   * The entry is read off the live URL and taken off it once arrived through
   * (2026-09-30): an arrival stands until the person speaks and stacks after,
   * so a reload or a remount that carried the entry again would have Ember
   * greet them from the light a second time. With nothing consumed left on
   * the URL, every later opening is the plain home resume.
   */
  async function boot({ arrive = true } = {}): Promise<void> {
    setInitFailed(false);
    if (previewMock) {
      setConv({ id: 'local-talk-preview', status: 'active', entryType: 'home', entryId: null, writtenThroughTurn: null, crisisReferredAt: null, harmReferredAt: null, crisisLines: null, ageCheck: null });
      setMessages(TALK_PREVIEW_MESSAGES);
      return;
    }
    const resumeId = resumeConversationId();
    const live = new URLSearchParams(window.location.search);
    const rawType = arrive ? (live.get('entry_type') ?? 'home') : 'home';
    const entryType: EntryType = (ENTRY_TYPES as string[]).includes(rawType) ? rawType as EntryType : 'home';
    const entryId = arrive ? live.get('entry_id') : null;
    try {
      // The newest THREAD_WINDOW turns of this person's conversation — the
      // page opens at its end, like any thread; older turns are read back as
      // the reader scrolls up (loadOlder), never up front.
      const resp = resumeId
        ? await emberApi.getConversation(resumeId, { limit: clientConfig().thread_window })
        : await emberApi.createConversation(entryType, entryType === 'home' ? null : entryId, false, { limit: clientConfig().thread_window });
      if (!aliveRef.current) return;
      if (!resumeId && (live.has('entry_type') || live.has('entry_id'))) {
        // The entry has been arrived through: off the URL, silently (replaceUrl
        // announces no navigation, so nothing remounts NOW). The conversation's
        // id is not written in its place — the next opening asks the edge for
        // this browser's conversation and gets this one back.
        const qs = new URLSearchParams(window.location.search);
        qs.delete('entry_type');
        qs.delete('entry_id');
        const query = qs.toString();
        replaceUrl(`${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`);
      }
      setConv({
        id: resp.conversation.id,
        status: resp.conversation.status,
        entryType: resp.conversation.entry_type,
        entryId: resp.conversation.entry_id,
        writtenThroughTurn: resp.conversation.written_through_turn ?? null,
        crisisReferredAt: resp.conversation.crisis_referred_at ?? null,
        harmReferredAt: resp.conversation.harm_referred_at ?? null,
        crisisLines: resp.crisis_lines ?? null,
        ageCheck: resp.age_check ?? null,
      });
      setMessages(resp.messages);
      setPhotoRoom(photoRoomOf(resp));
      notAddedRef.current = 0;
      setReplyIdle(resp.reply_in_flight === false);
      setHistory({ hasMore: resp.page.has_more, oldest: resp.page.oldest_turn_index, loading: false });
      setEmbers(resp.embers ?? []);
      setShareAsk(resp.viewer?.share_ask ?? null);
      setReads(resp.reads ?? []);
      setPrecedents(resp.precedents ?? []);
      setJoinCards(resp.join_cards ?? []);
      setFeatureCards(resp.feature_cards ?? []);
      setGuestDoor(resp.guest_door ?? null);
      setOpeningStarters(resp.opening_starters ?? null);
      setSilentStart(resp.silent_start ?? null);
      setViewerAvatar(resp.viewer?.avatar_id ?? '');
      if (legacyFinalized(resp.conversation.status)) {
        navigate(`/result/${resp.conversation.id}`, { replace: true });
      } else if (resp.conversation.status === 'failed') {
        // A legacy row whose finalize failed: the composer is live, and adding
        // to it is the repair — the next quiet hour writes it again.
        trackFailed('conversation_open', 'resumed_failed', { conversation_id: resp.conversation.id });
        toast.info('That last attempt broke on our end. Just keep telling — Ember will try again.');
      }
    } catch (err) {
      if (!aliveRef.current) return;
      // The free-telling budget is spent (§4.5 + the guest quota): this browser
      // has already told its free tellings, so the next one starts by
      // registering — the inbound-SMS door in place (§4.6.2, v3.0: never the
      // OTP /auth page). `auth_required` is the one-release legacy alias of
      // `registration_required` from conversation create. The
      // post-registration reload lands back on this exact URL, entry context
      // intact. Only the CREATE leg means this: a 401 on a resume is an
      // account session that died mid-telling, and that person has an account
      // to log back into (§12.7) — registration would mint them a second one.
      const bootErrCode = apiErrorCode(err);
      if (bootErrCode === 'not_served') {
        setNotServed(true);
        return;
      }
      if (!resumeId && (bootErrCode === 'registration_required' || bootErrCode === 'auth_required')) {
        trackFailed('talk_gate', 'free_telling_used');
        setNeedsRegistration('register');
        return;
      }
      if (!resumeId && bootErrCode === 'sign_in_required') {
        trackFailed('talk_gate', 'account_signed_out');
        setNeedsRegistration('sign_in');
        return;
      }
      if (resumeId && bootErrCode === 'auth_required') {
        trackFailed('conversation_open', 'auth_required', { conversation_id: resumeId });
        const back = `/talk${window.location.search || '?mode=chat'}`;
        navigate(`/auth?returnTo=${encodeURIComponent(back)}`);
        return;
      }
      // The URL names a conversation this browser cannot prove is its own: the
      // link was opened in another browser (the in-app browser's "open in
      // Safari", most often), or the cookie jar was reset since it was
      // created. No retry can change that answer, so the id comes off the
      // URL and this is the same boot, minus the resume (2026-09-03).
      if (resumeId && bootErrCode === 'not_found') {
        trackFailed('conversation_open', 'not_found', { conversation_id: resumeId });
        forgetConversationInUrl();
        return boot({ arrive });
      }
      // Not a failure and not the sign-in door: the visitor has simply not been
      // asked yet (or the record went stale). The door asks; this page does not.
      if (apiErrorCode(err) === 'consent_required') {
        navigate(doorUrl(), { replace: true });
        return;
      }
      trackFailed('conversation_open', 'load_failed', { conversation_id: resumeId || undefined, error_detail: apiFailureReason(err) });
      setInitFailed(true);
    }
  }

  /**
   * The id this page holds stopped naming this browser's conversation — a
   * poll, a send, a photo or a transcription came back `not_found`. The id
   * was only ever a pointer; the edge is the truth about which conversation
   * is this browser's (2026-09-16 拍板, §4.5). So the pointer comes off the
   * URL and the page opens that conversation again: after a sign-in it is the
   * account's one conversation, this guest thread now folded into it (prod
   * 2026-09-16 — a second browser on the same phone signed in from the
   * receipt's link, and the page it had open said the conversation could not
   * continue); after a cookie reset it is a fresh one. The thread on screen
   * stays until the reopened one replaces it, so a draft in the composer
   * survives. Once per refusal: a reopened conversation that is refused too,
   * before it was ever read back, is a browser that cannot hold one, and
   * the plain failure card takes over. The refusal is also the page's one
   * sign that who this browser is may have changed — the sign-in arrived in
   * another tab, and the session provider re-reads /api/me only on its own
   * door's event — so it is asked to re-read here: the parts of this page
   * drawn for a signed-out person (the text door) follow the account.
   */
  function reopen(): void {
    if (reopenedRef.current) {
      setInitFailed(true);
      return;
    }
    reopenedRef.current = true;
    forgetConversationInUrl();
    void refreshSession();
    void boot({ arrive: false });
  }

  /**
   * Upload one picked photo. The tray shows it immediately from a local object
   * URL; when the edge answers, that URL is swapped for the hosted one and
   * revoked, so the optimistic bubble already renders the stored image.
   * A refusal removes the item and says why — the photo was never kept.
   */
  const handlePickPhoto = async (file: File): Promise<'added' | 'over_cap' | 'failed'> => {
    if (!conv) return 'failed';
    const key = `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const objectUrl = URL.createObjectURL(file);
    setPhotos((p) => [...p, { key, previewUrl: objectUrl, uploading: true, mediaId: '' }]);
    // Same guard every other call in this page carries: the preview
    // conversation has no row behind it, so uploading against its id can only
    // 404. The tray still fills, which is the point of the preview.
    if (previewMock) {
      window.setTimeout(() => {
        if (!aliveRef.current) return;
        setPhotos((p) => p.map((item) => (item.key === key
          ? { ...item, uploading: false, mediaId: `preview_${key}` }
          : item)));
      }, 400);
      return 'added';
    }
    try {
      const stored = await emberApi.uploadConversationMedia(conv.id, file);
      if (!aliveRef.current) { URL.revokeObjectURL(objectUrl); return 'added'; }
      URL.revokeObjectURL(objectUrl);
      takeRoom(stored);
      // Taken out of the tray while it uploaded: it goes from the edge too, and its slot is free again.
      if (removedKeysRef.current.delete(key)) {
        emberApi.removeConversationMedia(conv.id, stored.media_id).then(takeRoom).catch(() => { /* counted until written up */ });
        return 'added';
      }
      setPhotos((p) => p.map((item) => (item.key === key
        ? { ...item, previewUrl: stored.url, uploading: false, mediaId: stored.media_id }
        : item)));
      return 'added';
    } catch (err) {
      URL.revokeObjectURL(objectUrl);
      removedKeysRef.current.delete(key);
      if (!aliveRef.current) return 'failed';
      setPhotos((p) => p.filter((item) => item.key !== key));
      const code = apiErrorCode(err);
      // The stretch filled while this one was on its way (another device, a text, the batch racing the room
      // read): the batch says it once (handlePickPhotos), with the photos the picker was trimmed by.
      if (code === 'over_cap') {
        const room = photoRoomOfError(err);
        if (room) setPhotoRoom(room);
        return 'over_cap';
      }
      if (code === 'content_rejected') {
        trackFailed('chat_photo', 'content_rejected', { conversation_id: conv.id });
        toast.error("That photo isn't something a telling can hold, so it wasn't saved.");
      } else if (code === 'media_unavailable') {
        trackFailed('chat_photo', 'media_unavailable', { conversation_id: conv.id });
        toast.error('Photos are unavailable right now. Your words still work.');
      } else if (code === 'not_found') {
        trackFailed('chat_photo', 'not_found', { conversation_id: conv.id });
        reopen();
      } else if (code === 'conversation_not_active') {
        trackFailed('chat_photo', 'conversation_not_active', { conversation_id: conv.id });
        toast.error('This conversation is closed.');
      } else if (code && code.startsWith('image_')) {
        trackFailed('chat_photo', 'image_error', { conversation_id: conv.id });
        toast.error("That file didn't look like a photo we can read.");
      } else {
        // Never swallow an unrecognised failure again: the first version of
        // this branch hid a plain 404 behind "try again" and cost a debugging
        // round. The person still gets the plain sentence. What it died of rides the event (2026-10-30 prod: six
        // photos failed in a Facebook in-app browser and the rows could only say unknown).
        console.error('ember: photo upload failed', { code: code || '(none)', err });
        trackFailed('chat_photo', 'unknown', { conversation_id: conv.id, error_detail: apiFailureReason(err) });
        toast.error("Couldn't add that photo. Try again.");
      }
      return 'failed';
    }
  };

  /** The + was tapped: the room left is read while the picker is open (the batch is trimmed by it). */
  const handlePickerOpen = (): void => {
    if (!conv || previewMock) return;
    setComposerTouched(true);
    const id = conv.id;
    roomReadRef.current = emberApi.getPhotoRoom(id)
      .then((body) => {
        const room = photoRoomOf(body);
        if (room && aliveRef.current && shownConvRef.current === id) setPhotoRoom(room);
        return room;
      })
      .catch(() => null);
  };

  /**
   * One picked batch (2026-10-19 Riley): what fits in the room the edge says is left goes up; the rest — and
   * any the upload still refused for the cap — is said once for the batch, and rides the next turn to Ember.
   */
  const handlePickPhotos = async (files: File[]): Promise<void> => {
    if (!conv) return;
    const read = roomReadRef.current;
    roomReadRef.current = null;
    const room = (read ? await read : null) ?? photoRoomRef.current ?? { used: 0, limit: clientConfig().photos_per_conversation };
    if (!aliveRef.current) return;
    const uploading = photosRef.current.filter((p) => p.uploading).length;
    // One message carries at most PHOTOS_PER_MESSAGE (2026-10-19 Riley): what is past it is not taken — said once, and
    // not counted as refused by the cap (the room is still there for the next message).
    const perMessageCap = clientConfig().photos_per_message;
    const perMessage = Math.max(0, perMessageCap - photosRef.current.length);
    const batch = files.slice(0, perMessage);
    if (files.length > batch.length) toast.info(`You can add up to ${perMessageCap} photos to one message.`);
    const fits = Math.max(0, room.limit - room.used - uploading);
    const outcomes = await Promise.all(batch.slice(0, fits).map((file) => handlePickPhoto(file)));
    const refused = batch.length - Math.min(batch.length, fits) + outcomes.filter((o) => o === 'over_cap').length;
    if (refused === 0 || !aliveRef.current) return;
    notAddedRef.current += refused;
    trackFailed('chat_photo', 'over_cap', { conversation_id: conv.id });
    toast.error(`${refused === 1 ? "1 photo wasn't" : `${refused} photos weren't`} added: this conversation is full until Ember writes it up.`);
  };

  /**
   * Voice input: the recorded clip goes to the edge, its words come back for
   * the composer. Nothing is stored either way. Resolves null on failure and
   * says why here — the shell only needs to know there is no text to place.
   */
  const handleTranscribe = async (audio: Blob): Promise<string | null> => {
    if (!conv) return null;
    if (previewMock) {
      await new Promise((r) => { window.setTimeout(r, 700); });
      return 'It was the summer my grandmother stopped recognising the house.';
    }
    try {
      const { text } = await emberApi.transcribeAudio(conv.id, audio);
      if (!aliveRef.current) return null;
      if (!text) {
        trackFailed('chat_transcribe', 'empty', { conversation_id: conv.id });
        toast.error("We couldn't make out any words in that one. Try once more?");
        return null;
      }
      return text;
    } catch (err) {
      if (!aliveRef.current) return null;
      const code = apiErrorCode(err);
      if (code === 'not_found') {
        trackFailed('chat_transcribe', 'not_found', { conversation_id: conv.id });
        reopen();
      } else if (code === 'conversation_not_active') {
        trackFailed('chat_transcribe', 'conversation_not_active', { conversation_id: conv.id });
        toast.error('This conversation is closed.');
      } else if (code === 'transcription_unavailable') {
        trackFailed('chat_transcribe', 'transcription_unavailable', { conversation_id: conv.id });
        toast.error('Voice input is unavailable right now. Your words still work.');
      } else {
        console.error('ember: transcription failed', { code: code || '(none)', err });
        trackFailed('chat_transcribe', apiFailureReason(err), { conversation_id: conv.id });
        toast.error("Couldn't turn that into words. Try again, or type it.");
      }
      return null;
    }
  };

  const handleMicUnavailable = (reason: MicUnavailableReason): void => {
    if (!conv) return;
    trackFailed('chat_transcribe', reason === 'denied' ? 'mic_denied' : 'mic_unsupported', { conversation_id: conv.id });
    toast.error(reason === 'denied'
      ? 'We need microphone permission to hear you. Typing works just as well.'
      : "This browser can't record audio. Typing works just as well.");
  };

  const handleRemovePhoto = (key: string): void => {
    // A photo the edge holds is deleted there (2026-10-19 Riley), so it stops holding a slot; one still
    // uploading is dropped when its upload lands (handlePickPhoto).
    const item = photosRef.current.find((x) => x.key === key);
    if (item && conv && !previewMock) {
      if (item.uploading) removedKeysRef.current.add(key);
      else if (item.mediaId) emberApi.removeConversationMedia(conv.id, item.mediaId).then(takeRoom).catch(() => { /* counted until written up */ });
    }
    setPhotos((p) => {
      const going = p.find((item) => item.key === key);
      // An uploaded item points at Cloudinary and has nothing to release; a
      // pending one — or any preview item — still holds a blob: URL.
      if (going?.previewUrl.startsWith('blob:')) URL.revokeObjectURL(going.previewUrl);
      return p.filter((item) => item.key !== key);
    });
  };

  const conversationOpen = !!conv && (conv.status === 'active'
    || conv.status === 'needs_more_context'
    || conv.status === 'failed');

  // The next arrival: every whole Ember not yet presented, in one ceremony
  // (2026-09-15: a call that wrote two Embers used to raise the tree over the
  // chat twice in ten seconds), once nothing else is playing or open, and
  // only while the page is in front of the person — a tab in the background
  // waits for them to come back to it.
  useEffect(() => {
    if (arrival || reading) return undefined;
    const next = embers.filter((e) => !e.presented_at && !presented.has(e.id) && emberIsWhole(e));
    if (next.length === 0) return undefined;
    const start = (): void => setArrival({ ids: next.map((e) => e.id), stage: 'ceremony' });
    if (document.visibilityState === 'visible') { start(); return undefined; }
    const onVisible = (): void => { if (document.visibilityState === 'visible') start(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [embers, presented, arrival, reading]);
  // Landed: the card is in, the light walks its edge (3s, CSS), the edge is
  // stamped. The stamp failing only means this Ember plays once more on the
  // next visit — recorded, not retried.
  const arrivalRef = useRef(arrival);
  arrivalRef.current = arrival;
  const conversationIdRef = useRef<string | null>(null);
  conversationIdRef.current = conv?.id ?? null;
  const onArrivalLanded = useCallback((): void => {
    const a = arrivalRef.current;
    const convId = conversationIdRef.current;
    if (!a) return;
    const landedNow = { ids: a.ids, stage: 'landed' as const };
    setArrival(landedNow);
    setPresented((p) => new Set([...p, ...a.ids]));
    if (convId) {
      for (const id of a.ids) {
        emberApi.markEmberPresented(convId, id).catch(() => {
          trackFailed('ember_presented', 'request_failed', { ember_id: id, conversation_id: convId });
        });
      }
    }
    window.setTimeout(() => setArrival((cur) => (cur === landedNow ? null : cur)), 3400);
  }, []);
  // Once the arrival has cleared — the card is in, the light has walked its
  // edge, the night is gone — ask the edge whether it is time to put the one
  // question (first Ember, then every five and fourteen days; the rule is
  // the edge's). `ask: null` is the ordinary answer. Never over the ceremony
  // itself: the row rises into a thread that has come to rest.
  const arrivalBefore = useRef(arrival);
  useEffect(() => {
    const before = arrivalBefore.current;
    arrivalBefore.current = arrival;
    if (arrival !== null || before?.stage !== 'landed' || !conv || previewMock) return;
    const convId = conv.id;
    emberApi.openHelpAsk(convId)
      .then((r) => { if (aliveRef.current && r.ask) setHelpAsk({ id: r.ask.id }); })
      .catch(() => trackFailed('help_ask', 'request_failed', { conversation_id: convId }));
  }, [arrival, conv, previewMock]);
  const closeHelpAsk = useCallback((outcome: HelpAskOutcome, answer?: HelpAskAnswer): void => {
    const convId = conversationIdRef.current;
    const ask = helpAsk;
    if (!convId || !ask) return;
    emberApi.closeHelpAsk(convId, ask.id, outcome, answer)
      .catch(() => trackFailed('help_ask', 'close_failed', { conversation_id: convId, outcome }));
  }, [helpAsk]);
  // E09 for a text telling (§4.7 → §4.5, 2026-09-12): the Ember lives in this
  // browser until the person joins; one text keeps it. Under the card and
  // under the page alike — the sentence, then the door, as two items of the
  // note's column (the containers lay them out; see .convo-ember-note).
  const emberNote = sessionStatus === 'authenticated' ? undefined : (
    <>
      <span>Right now this lives only in this browser. One text and it&rsquo;s yours on every device.</span>
      <InboundRegistrationCTA logName="talk_ember_join" label="Join Ember" />
    </>
  );
  // The same conversation on their phone (2026-09-16, §4.5; 2026-10-01 拍板:
  // one place, always in sight; 2026-10-01 二次拍板: one chip, one label). A
  // signed-out person's door stands as one chip over the field for the whole
  // conversation — so whenever Ember says something is there for signed-in
  // people, or offers the way to stay connected when a guest says thanks, the
  // way to it is under the next word they type. It does not show beside an
  // Ember card, which carries its own note, and it goes with joining. Nothing
  // is gated — the composer stays live. The label is the one Ember's prompt
  // quotes (brain renderDoorsBlock), so the page and Ember say one thing;
  // "Only in this browser for now" went with the chip (prod 09-19→10-01: 13
  // grateful guests, 0 taps on the grey line).
  const textDoor = (
    <InboundRegistrationCTA
      logName="talk_text_door"
      discovery="composer"
      seen
      holdsWait
      label="Join and stay connected with Ember →"
      icon={<svg className="convo-text-door-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M11 18h2" /></svg>}
      className="convo-text-door"
    />
  );
  // It stays beside a card to join (2026-10-11, dev e2e): the talk model knows this chip from the doors block and points to
  // it in the very reply that answered a guest's yes — the card and the chip are the same door.
  // Not while their age is being asked, nor once they answered under 18 (2026-10-30 e2e: a guest just closed as under 18
  // was still offered the way to join).
  const composerLine = sessionStatus === 'anonymous' && shownEmbers.length === 0 && !conv?.ageCheck ? textDoor : null;

  // A guest's invite and door (2026-10-27 founder; GuestDoor.tsx). The invite is due once their messages reach the
  // edge's count while the door does not stand yet and they have not said Maybe later; it is drawn after the newest of
  // Ember's replies at the moment it became due, and stays there. The door stands while the edge says so.
  const inviteDue = sessionStatus === 'anonymous' && guestDoor !== null && !guestDoor.open && guestDoor.said >= guestDoor.invite_at && !inviteLater;
  useEffect(() => {
    if (!inviteDue || inviteAfter !== null) return;
    const last = [...messages].reverse().find((m) => m.role === 'assistant');
    if (last) setInviteAfter(last.turn_index);
  }, [inviteDue, inviteAfter, messages]);
  const guestLogAttrs = conv ? { 'data-log-conversation-id': conv.id } : {};
  const guestInvite = inviteDue && inviteAfter !== null ? {
    after: inviteAfter,
    node: <GuestInviteCard logAttrs={guestLogAttrs} onLater={() => { setInviteLater(true); writeInviteLater(); }} />,
  } : null;
  const composerDoor = sessionStatus === 'anonymous' && guestDoor?.open === true
    ? <GuestDoorPanel signIn={guestDoor.sign_in} logAttrs={guestLogAttrs} />
    : undefined;

  /**
   * One turn out, the reply back as it is written (2026-09-21). `revise` is
   * Try again under a reply that failed: the words are the turn already in
   * the thread at that index — nothing is added to the thread, and Ember
   * answers what is there.
   */
  const handleSend = async (text: string, mediaIds: string[] = [], about: TurnAbout | null = null, revise: number | null = null): Promise<boolean> => {
    if (!conv || sending) return false;
    const sentPhotos = revise === null ? photos.filter((p) => !p.uploading && mediaIds.includes(p.mediaId)) : [];
    const lastIndex = messages.length > 0 ? messages[messages.length - 1].turn_index : -1;
    const existing = revise === null ? null : messages.find((m) => m.role === 'user' && m.turn_index === revise) ?? null;
    // The turn's key: the one its words came back to the field with, the one kept for the turn Try again re-asks, else new.
    const turnKey = revise === null
      ? (restoredKey.current && restoredKey.current.text === text ? restoredKey.current.key : newIdempotencyKey())
      : (turnKeys.current.get(revise) ?? newIdempotencyKey());
    restoredKey.current = null;
    const optimistic: ConversationMessage = existing ?? {
      role: 'user',
      content: text,
      turn_index: lastIndex + 1,
      attachments: sentPhotos.map((p) => ({
        media_id: p.mediaId, url: p.previewUrl, width: null, height: null,
      })),
    };
    // The tray empties with the turn: those photos now belong to a message.
    // A failed send restores them alongside the restored text.
    if (sentPhotos.length > 0) setPhotos((p) => p.filter((item) => item.uploading));
    if (!existing) setMessages((m) => [...m, optimistic]);
    turnKeys.current.set(optimistic.turn_index, turnKey);
    setFailedTurn(null);
    setStoppedTurn(null);
    setSending(true);
    // The reply as Ember writes it: one live assistant turn after the
    // person's, grown delta by delta, replaced by the finished turn when it
    // lands — or taken out again on Stop or a failure (the edge kept nothing).
    const liveIndex = optimistic.turn_index + 1;
    let live: ConversationMessage | null = null;
    const setLive = (next: ConversationMessage): void => {
      live = next;
      setMessages((m) => [...m.filter((x) => x.turn_index !== liveIndex), next].sort((a, b) => a.turn_index - b.turn_index));
    };
    const dropLive = (): void => {
      if (!live) return;
      live = null;
      setMessages((m) => m.filter((x) => !(x.role === 'assistant' && x.turn_index === liveIndex)));
    };
    if (previewMock) {
      window.setTimeout(() => {
        if (!aliveRef.current) return;
        setMessages((m) => [...m, {
          role: 'assistant',
          content: 'What feels most important about that part — the thing you would want someone else to understand without having to explain it twice?',
          turn_index: optimistic.turn_index + 1,
        }]);
        setSending(false);
      }, 650);
      return true;
    }
    const ac = new AbortController();
    abortRef.current = ac;
    inFlightTurnRef.current = optimistic.turn_index;
    // The request has no timeout of its own, and an instance switching under
    // it (a deploy) can leave it pending forever — which kept `sending` true
    // and the composer dead (prod 2026-09-08 16:07). This abort is the page's
    // own, told apart from Stop and from leaving by `timedOut`.
    let timedOut = false;
    const sendTimer = window.setTimeout(() => {
      timedOut = true;
      ac.abort();
    }, SEND_TIMEOUT_MS);
    // Did the turn land anyway? The edge stores the words before it asks for
    // the reply (appendMessage), so a send this page gave up on, or one that
    // died on the reply, may have left them in the transcript all the same.
    // One bounded read of the transcript says which: 'answered' — the words and
    // a reply after them are there, and the transcript was taken; 'unanswered'
    // — the words are there without a reply, transcript taken; false — the
    // turn is not there.
    const resyncLanded = async (): Promise<'answered' | 'unanswered' | false> => {
      try {
        // The turns from the person's onward: what the edge kept of this
        // send, and anything that landed after it (a text from their phone).
        const fresh = await Promise.race([
          emberApi.getConversation(conv.id, { after: optimistic.turn_index - 1 }),
          new Promise<never>((_, reject) => {
            window.setTimeout(() => reject(new Error('resync_timeout')), SEND_RESYNC_RACE_MS);
          }),
        ]);
        // The page has moved on; nothing here is left to say or to restore.
        if (!aliveRef.current || shownConvRef.current !== conv.id) return 'answered';
        const landed = fresh.messages.some((m) => m.role === 'user' && m.turn_index === optimistic.turn_index);
        if (!landed) return false;
        setMessages((m) => mergeTurns(m.filter((x) => x.turn_index < optimistic.turn_index), fresh.messages));
        const answered = fresh.messages.some((m) => m.role === 'assistant' && m.turn_index > optimistic.turn_index);
        return answered ? 'answered' : 'unanswered';
      } catch {
        return false;
      }
    };
    // After a Stop, the edge is the truth about what was kept: wait for the
    // stop to be acknowledged, then take the transcript as it stands.
    const resyncAfterStop = async (): Promise<void> => {
      await stopRef.current?.done;
      try {
        // From the person's turn onward: what the edge kept after the stop.
        // Everything the page held from that turn on is replaced by it —
        // a reply that was deleted is gone, one that was kept is there.
        const fresh = await emberApi.getConversation(conv.id, { after: optimistic.turn_index - 1 });
        // Not just "is the page still here": the telling it is showing may
        // have changed under this read (the New door), and this transcript
        // belongs to the one that was put down.
        if (!aliveRef.current || shownConvRef.current !== conv.id) return;
        setMessages((m) => mergeTurns(m.filter((x) => x.turn_index < optimistic.turn_index), fresh.messages));
        // The words were kept and nothing answers them: say so under them, with the way to an answer.
        const kept = fresh.messages.some((x) => x.role === 'user' && x.turn_index === optimistic.turn_index);
        const answered = fresh.messages.some((x) => x.role === 'assistant' && x.turn_index > optimistic.turn_index);
        if (kept && !answered) setStoppedTurn(optimistic.turn_index);
        // Nothing of the turn reached the edge: the words come back to the field, with their photos, and the page says so.
        if (!kept && !existing) {
          restoredKey.current = { text, key: turnKey };
          setRestoredDraft({ text, about });
          if (sentPhotos.length > 0) setPhotos((p) => [...sentPhotos, ...p]);
          toast.info('Stopped before it was sent. Your words are back in the field.');
        }
      } catch {
        // Local state kept; the transcript may now differ from the edge's.
        trackFailed('chat_send', 'resync_failed', { conversation_id: conv.id });
      }
    };
    // The photos the cap refused since the last turn ride this one (2026-10-19 Riley); a re-ask carries none.
    const notAdded = revise === null ? notAddedRef.current : 0;
    const onLiveFace = (face: string): void => {
      if (!aliveRef.current || shownConvRef.current !== conv.id) return;
      setLive({ role: 'assistant', content: live?.content ?? '', turn_index: liveIndex, face: face as MascotFace, attachments: [] });
    };
    const onLiveDelta = (delta: string): void => {
      if (!aliveRef.current || shownConvRef.current !== conv.id) return;
      setLive({ role: 'assistant', content: (live?.content ?? '') + delta, turn_index: liveIndex, ...(live?.face ? { face: live.face } : {}), attachments: [] });
    };
    // The channel joined: the words come on it (keyed by this turn's key); otherwise on the POST's own stream —
    // one of the two, never both. The edge publishes every reply to the channel whatever the door asked, so a send
    // that streams on the POST takes nothing from the channel: joined mid-reply (the homepage's first line is sent
    // while the page is still joining), both fed the bubble and the words came out twice, interleaved (dev 10-22).
    const liveOn = liveConv.current?.active === true;
    if (liveOn) replyHandlers.current.set(turnKey, { seq: 0, delta: onLiveDelta, face: onLiveFace });
    try {
      const r = await emberApi.sendMessage(conv.id, text, mediaIds, {
        signal: ac.signal,
        turnKey,
        live: liveOn,
        ...(notAdded > 0 ? { photosNotAdded: notAdded } : {}),
        ...(revise !== null ? { reviseTurnIndex: revise } : {}),
        ...(about ? { about } : {}),
        onFace: (face) => onLiveFace(face),
        onDelta: (delta) => onLiveDelta(delta),
      });
      replyHandlers.current.delete(turnKey);
      if (!aliveRef.current) return true;
      // The edge's index for the turn (an interjection may have taken the optimistic one): Try again looks it up by that.
      turnKeys.current.set(r.user_turn.turn_index, turnKey);
      notAddedRef.current = Math.max(0, notAddedRef.current - notAdded);
      // The reply beat the abort to the wire, but Stop was pressed: never draw
      // it — the edge is deleting or has skipped it.
      const stop = stopRef.current;
      if (stop && stop.turnIndex === optimistic.turn_index) {
        dropLive();
        await resyncAfterStop();
        return true;
      }
      live = null;
      setMessages((m) => {
        const byIndex = new Map<number, ConversationMessage>();
        // On a re-ask the optimistic turn IS the turn already shown (`existing`), and it stays: dropping it here
        // and not putting the edge's copy back took the person's words off the screen whenever Try again or
        // Let Ember reply got its answer, until the next reload (dev walkthrough 2026-10-17).
        for (const msg of m) {
          if (existing || msg !== optimistic) byIndex.set(msg.turn_index, msg);
        }
        // On Try again the turn shown keeps its photos; the edge's copy of a
        // revised turn carries none (nothing was attached this time).
        if (!existing) byIndex.set(r.user_turn.turn_index, r.user_turn);
        if (r.assistant_turn) byIndex.set(r.assistant_turn.turn_index, r.assistant_turn);
        else byIndex.delete(liveIndex);
        return [...byIndex.values()].sort((a, b) => a.turn_index - b.turn_index);
      });
      // Overtaken (2026-10-18): the newer turn's reply is on its way and the next poll brings both — no Let Ember reply meanwhile.
      if (r.overtaken) setReplyIdle(false);
      if (r.guest_door) setGuestDoor(r.guest_door);
      // The age check stands (edge age-check.js): the words were kept, nothing answers them; its question is drawn.
      // Ember's question landed after the earlier reply, from the recorder — a page with no live channel (a guest) has
      // not read it yet, so the thread's newest turns are read again and the question stands above the card.
      if (r.age_check) {
        setConv((c) => (c ? { ...c, ageCheck: r.age_check ?? null } : c));
        void emberApi.getConversation(conv.id, { limit: clientConfig().thread_window })
          .then((fresh) => { if (aliveRef.current) setMessages((m) => mergeTurns(m, fresh.messages)); })
          .catch(() => {});
      }
      setConv((c) => (c && c.status === 'needs_more_context' ? { ...c, status: 'active' } : c));
      reopenedRef.current = false;
      return true;
    } catch (err) {
      replyHandlers.current.delete(turnKey);
      if (!aliveRef.current) return true;
      // Whatever streamed was never kept: the edge stores only a finished reply.
      dropLive();
      if (ac.signal.aborted && !timedOut) {
        // Only a Stop asks what the edge kept. An abort with no Stop behind it
        // is this page letting the turn go — leaving, or putting the telling
        // down for a new one — and re-reading the transcript it just left is
        // at best wasted and at worst drawn into the telling that replaced it.
        const stopped = stopRef.current;
        if (stopped && stopped.turnIndex === optimistic.turn_index) await resyncAfterStop();
        return true;
      }
      // The reason recorded is apiFailureReason (2026-09-17): the edge's code
      // when it refused with one, else the HTTP status, else the browser's own
      // message — the bare network_error hid which of the three it was.
      const code = timedOut ? 'timeout' : apiFailureReason(err);
      // The guest door (2026-10-27 founder): the edge refused a new turn from a guest whose budget is spent, before
      // storing anything — the words go back into the field for after they join, and the door stands where they type.
      // A browser stopped as under 18 (edge age-check.js): nothing was stored; the page becomes the minor notice.
      if (code === 'not_served') {
        setNotServed(true);
        trackFailed('chat_send', code, { conversation_id: conv.id });
        return false;
      }
      if (code === 'registration_required' || code === 'sign_in_required') {
        setMessages((m) => m.filter((x) => x !== optimistic));
        if (sentPhotos.length > 0) setPhotos((p) => [...sentPhotos, ...p]);
        restoredKey.current = { text, key: turnKey };
        setGuestDoor((d) => ({ said: d?.said ?? 0, invite_at: d?.invite_at ?? 0, door_at: d?.door_at ?? 0, open: true, sign_in: code === 'sign_in_required' }));
        trackFailed('chat_send', code, { conversation_id: conv.id });
        return false;
      }
      // The edge refused before storing anything: the turn is not there.
      if (code === 'conversation_not_active') {
        setMessages((m) => m.filter((x) => x !== optimistic));
        trackFailed('chat_send', code, { conversation_id: conv.id });
        toast.error('This conversation is closed.');
        return true;
      }
      // The edge refused before storing anything, and the id is the problem:
      // the words come back into the composer while the page reopens this
      // browser's conversation, and one tap sends them into it.
      if (code === 'not_found') {
        setMessages((m) => m.filter((x) => x !== optimistic));
        if (sentPhotos.length > 0) setPhotos((p) => [...sentPhotos, ...p]);
        trackFailed('chat_send', code, { conversation_id: conv.id });
        reopen();
        return false;
      }
      // The edge is still answering this very turn (the keyed retry met its
      // first copy, 2026-10-01): wait for the reply to land, then take the
      // transcript as it stands. Past the wait, the turn is there without a
      // reply and Try again stands under it, as for any other unanswered turn.
      if (code === 'in_progress') {
        const until = Date.now() + IN_PROGRESS_WAIT_MS;
        while (Date.now() < until) {
          await new Promise((resolve) => { window.setTimeout(resolve, IN_PROGRESS_POLL_MS); });
          if (!aliveRef.current || shownConvRef.current !== conv.id) return true;
          if ((await resyncLanded()) === 'answered') return true;
        }
      }
      // Everything else died somewhere between this page and the reply — the
      // page gave up on the wire, the model answered nothing (prod 2026-09-14:
      // the words were stored, the page said "Couldn't send", and the person
      // asked whether they had said something wrong), the reply failed after
      // its first words, the connection dropped on the way back. The words
      // may be there without a reply; the edge is the truth about what it
      // kept. A landed turn stays, the transcript is taken as it stands, and
      // Try again stands under the turn (2026-09-21) — Ember answers what is
      // there; one the edge never got comes back into the composer to send again.
      const landed = await resyncLanded();
      if (landed) {
        if (landed === 'unanswered') {
          trackFailed('chat_send', code, { conversation_id: conv.id });
          setFailedTurn(optimistic.turn_index);
        }
        return true;
      }
      if (existing) {
        // The turn Try again was for is no longer there (the thread moved on
        // under it); the transcript just read is the truth.
        return true;
      }
      setMessages((m) => m.filter((x) => x !== optimistic));
      // The photos are still stored and still unsent — put them back in the
      // tray with the text so one tap retries the whole turn — and the turn's
      // key with them: the edge may hold the turn after all (a look at the
      // transcript that timed out), and the same key makes the next tap that
      // turn, not a second copy of it.
      if (sentPhotos.length > 0) setPhotos((p) => [...sentPhotos, ...p]);
      restoredKey.current = { text, key: turnKey };
      trackFailed('chat_send', code, { conversation_id: conv.id });
      toast.error(timedOut ? 'That took too long to send. Try again.' : "Couldn't send. Try again.");
      return false;
    } finally {
      window.clearTimeout(sendTimer);
      abortRef.current = null;
      inFlightTurnRef.current = null;
      stopRef.current = null;
      if (aliveRef.current) setSending(false);
    }
  };

  /**
   * Stop, as the person means it: the reply to what they just said is not
   * wanted — not shown, not kept. The edge hears it first; the request drop
   * follows. An abort with no turn in flight (nothing to stop) just aborts.
   */
  /**
   * Try again under a reply that failed (2026-09-21): Ember answers the turn
   * already in the thread. One look at the transcript first — a reply may
   * have landed since (a slow one, or by text) — and then the same words go
   * as a revise of that turn, so no copy of it is added.
   */
  const handleRetry = async (turnIndex: number | null = failedTurn): Promise<void> => {
    if (!conv || sending || turnIndex === null) return;
    const turn = messages.find((m) => m.role === 'user' && m.turn_index === turnIndex);
    if (!turn) { setFailedTurn(null); setStoppedTurn(null); return; }
    try {
      const fresh = await emberApi.getConversation(conv.id, { after: turnIndex });
      if (!aliveRef.current || shownConvRef.current !== conv.id) return;
      if (fresh.messages.length > 0) {
        setMessages((m) => mergeTurns(m, fresh.messages));
        setFailedTurn(null);
        setStoppedTurn(null);
        return;
      }
    } catch {
      // Unreadable right now; the retry itself will say.
    }
    await handleSend(turn.content, [], null, turnIndex);
  };

  /**
   * The reach back (thread-window.ts): the THREAD_WINDOW turns older than the
   * oldest one here, when the reader scrolls to the top of what the page
   * holds. One at a time; a failed read is the next scroll's to retry. The
   * shell keeps the reader's place across the prepend.
   */
  const loadOlder = async (): Promise<void> => {
    const h = historyRef.current;
    if (!conv || previewMock || !h.hasMore || h.loading || h.oldest === null) return;
    // Said on the ref at once: the scroller asks on every scroll event, and
    // the state that says "loading" lands a render later.
    historyRef.current = { ...h, loading: true };
    setHistory({ ...h, loading: true });
    try {
      const older = await emberApi.getConversation(conv.id, { limit: clientConfig().thread_window, before: h.oldest });
      if (!aliveRef.current || shownConvRef.current !== conv.id) return;
      setMessages((m) => mergeTurns(m, older.messages));
      setEmbers((e) => mergeEmbers(e, older.embers ?? []));
      setHistory({ hasMore: older.page.has_more, oldest: older.page.oldest_turn_index ?? h.oldest, loading: false });
    } catch {
      if (!aliveRef.current) return;
      trackFailed('conversation_open', 'history_failed', { conversation_id: conv.id });
      setHistory((cur) => ({ ...cur, loading: false }));
    }
  };

  const handleStop = (): void => {
    const turnIndex = inFlightTurnRef.current;
    if (conv && turnIndex !== null && !previewMock) {
      stopRef.current = {
        turnIndex,
        done: emberApi.stopReply(conv.id, turnIndex).catch(() => {
          // The person pressed Stop and the edge never heard it.
          trackFailed('chat_send', 'stop_failed', { conversation_id: conv.id });
          return undefined;
        }),
      };
    }
    abortRef.current?.abort();
  };

  // The conversation with Ember on screen (2026-10-20): the edge is told how far it has been seen, so its row in the
  // Chats list counts only what came after. The newest of the edge's turns — never while a send holds optimistic ones.
  const seenTurn = messages.length > 0 ? messages[messages.length - 1].turn_index : null;
  useEmberSeen(conv?.id ?? null, seenTurn, sessionStatus === 'authenticated' && !roomInFront && !sending && !previewMock);

  if (notServed) {
    return (
      <TalkFrame returnTo={returnTo}>
        <MinorNotice />
      </TalkFrame>
    );
  }
  if (initFailed) {
    return (
      <TalkFrame returnTo={returnTo}>
        <div className="ember-page-quiet talk-error">
          <p>Couldn't open the conversation.</p>
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="talk_boot_retry"
            onClick={() => { void boot(); }}
          >
            Try again
          </button>
        </div>
      </TalkFrame>
    );
  }

  // The free budget is spent: the composer's slot holds the registration door
  // instead (§4.5).
  if (needsRegistration) {
    const signIn = needsRegistration === 'sign_in';
    return (
      <TalkFrame returnTo={returnTo}>
        {/* data-log-state names the surface: the seen dedup key is
            name+props, so without it whichever of the chooser/chat gates
            rendered first swallowed the other's exposure for the visit. */}
        <div className="talk-consent talk-register" data-log-seen="talk_quota_gate" data-log-state={signIn ? 'talk_sign_in' : 'talk'}>
          {signIn ? (
            <>
              <h1 className="talk-consent-title">Sign in to keep going</h1>
              <p className="talk-consent-copy">
                This browser belongs to an Ember account. Sign in with one text
                and pick up where you left off.
              </p>
              <InboundRegistrationCTA logName="talk_quota_sign_in" label="Sign in by text" />
            </>
          ) : (
            <>
              <h1 className="talk-consent-title">Join to keep going</h1>
              <p className="talk-consent-copy">
                Join with one text to keep going &mdash; and to keep what you&rsquo;ve told.
              </p>
              <InboundRegistrationCTA logName="talk_quota_register" />
            </>
          )}
        </div>
      </TalkFrame>
    );
  }

  const shareAskEmberId = shareAsk?.ember_id ?? '';

  // A room in front (§4.21): the same frame, the same thread shape, the
  // room's lines instead of the conversation's. The way back is the header's
  // Back to the Chats list (2026-10-20).
  if (roomInFront && sessionStatus === 'authenticated') {
    return (
      <TalkFrame returnTo={returnTo}>
        <RoomThread key={roomInFront} roomId={roomInFront} />
      </TalkFrame>
    );
  }

  // The person's words stand last and nothing is coming for them (replyIdle):
  // Let Ember reply under them, as after a Stop, once this page has no reason
  // of its own to say (no send on the wire, nothing failed or stopped here).
  const lastTurn = messages.length > 0 ? messages[messages.length - 1] : null;
  const unansweredTurn = replyIdle && !sending && failedTurn === null && stoppedTurn === null && lastTurn?.role === 'user'
    ? lastTurn.turn_index : null;

  if (!conv) {
    return (
      <TalkFrame returnTo={returnTo}>
        {/* The shared wait state instead of a blank frame while the
            conversation boots (UI standard §5). */}
        <PageLoading variant="inline" className="talk-opening" />
      </TalkFrame>
    );
  }

  // `failed` is open too (2026-08-19): adding to the telling is the repair.
  const composerDisabled = !conversationOpen;
  // Turns beyond the window count as said: a thread that reaches back past
  // what is held is not a first screen.
  const saidSomething = history.hasMore || messages.some((m) => m.role === 'user');
  // The three starters sit where the first user bubble will go and send on
  // tap; home arrivals only.
  // The silent start's line as the latest word (moments.js): its own three openers stand in.
  const starters = conv.entryType === 'home' && !saidSomething && !previewMock ? (openingStarters ?? TALK_STARTERS) : undefined;
  // The photo cap belongs to the stretch not yet written into an Ember; the edge counts it (photoRoom) — the
  // photos sent in it and the ones in the tray — and the uploads still on their way take a slot each here.
  const photoLimit = photoRoom?.limit ?? cfg.photos_per_conversation;
  const photosLeft = Math.max(0, photoLimit - (photoRoom?.used ?? 0) - photos.filter((p) => p.uploading).length);
  const photoFullLine = photoRoom && photoRoom.used >= photoRoom.limit && !previewMock
    ? `You've added ${photoRoom.limit} photos here. You can add more after Ember writes this up.`
    : null;

  // Go on out loud: /collect dials the in-browser call, which is a stretch of
  // this same conversation (2026-09-12 拍板: four doors, one history — the
  // person's one conversation is what a call joins, no id to carry), and its
  // wrap returns to this URL with the spoken turns already in it. Signed out,
  // the call is the guest trial (`?trial=1`), a stretch of this browser's
  // conversation since 2026-09-14 — the same door TalkDock offers a guest. A
  // full page load on purpose: the call surface is server-rendered, not an
  // SPA route (same reason as TalkDock's door).
  const talkReturnParams = new URLSearchParams({ mode: 'chat' });
  if (returnTo) talkReturnParams.set('returnTo', returnTo);
  const voiceCallHref = `/collect?${sessionStatus === 'authenticated' ? 'call=1' : 'trial=1'}&returnTo=${encodeURIComponent(`/talk?${talkReturnParams.toString()}`)}`;
  const voiceCallDoor = sessionStatus !== 'loading' && conversationOpen && !previewMock ? (
    <a
      className="talk-voice-call"
      href={voiceCallHref}
      aria-label="Call"
      data-log="talk_voice_continue"
      data-log-conversation-id={conv.id}
    >
      {/* A handset, not a microphone: the mic is what the composer's dictation
          button already means on this page (hold and speak into the message
          you are writing), and the same glyph twice for two different moves
          reads as the same move. A phone is the one icon that says "leave the
          typing and be on a call" without a word — the convention every chat
          app that offers voice has settled on. Since 2026-09-07 it is drawn
          once, in components/CallGlyph, for every voice door. */}
      <CallGlyph size={16} strokeWidth={1.7} />
      {/* "Call", not "Voice call" (2026-09-07): the handset beside it has
          already said "voice", and a phone's header row is where every word
          costs. */}
      <span>Call</span>
    </a>
  ) : undefined;

  return (
    <TalkFrame
      returnTo={returnTo}
      action={voiceCallDoor}
    >
      <ConversationShell
        messages={messages}
        typing={sending}
        disabled={composerDisabled}
        onSend={handleSend}
        draft={restoredDraft ?? draft}
        draftKey={`talk:${conv.id}`}
        onStop={handleStop}
        retry={conv.ageCheck ? null : failedTurn !== null
          ? { afterTurn: failedTurn, onRetry: () => { void handleRetry(failedTurn); } }
          : stoppedTurn !== null
            ? { afterTurn: stoppedTurn, onRetry: () => { void handleRetry(stoppedTurn); }, stopped: true }
            : unansweredTurn !== null
              ? { afterTurn: unansweredTurn, onRetry: () => { void handleRetry(unansweredTurn); }, unanswered: true }
              : null}
        starters={starters}
        onKeystroke={() => setComposerTouched(true)}
        onRecordStart={() => setComposerTouched(true)}
        photos={{
          pending: photos,
          remaining: photosLeft,
          onPickPhoto: async (file) => { await handlePickPhotos([file]); },
          onPickPhotos: (files) => { void handlePickPhotos(files); },
          onPickerOpen: handlePickerOpen,
          fullLine: photoFullLine,
          onRemovePhoto: handleRemovePhoto,
        }}
        voice={{ transcribe: handleTranscribe, onMicUnavailable: handleMicUnavailable }}
        logConversationId={conv.id}
        embers={shownEmbers}
        emberNote={emberNote}
        shareAsk={shareAsk ? {
          ...shareAsk,
          onAnswered: () => notifyKnocksChanged(),
          // The card's three answers are quick replies (2026-09-28): the words go
          // in as the person's line; Ember answers; the letter reads that line.
          // The answer itself goes on the record here too (2026-10-20): the reader
          // that reads it off the line runs after the reply, some 20 s later, and
          // until it had, the letter stood in the rail as still waiting (dev e2e).
          // Whichever of the two is second gets already_answered — the same fact.
          onReply: async (text: string, answer: ShareAnswer) => {
            const ok = await handleSend(text);
            if (ok) {
              try {
                await emberApi.answerShare(shareAskEmberId, answer);
              } catch (err) {
                const code = apiErrorCode(err);
                if (code !== 'already_answered') trackFailed('share_answer', code || 'error', { ember_id: shareAskEmberId });
              }
              notifyKnocksChanged();
              if (sessionStatus === 'authenticated') void refreshSession();
            }
            return ok;
          },
        } : null}
        reads={reads}
        precedents={precedents}
        // A card to join is for a guest: once signed in, what it waited on is given a moment later (releaseJoining),
        // and until the next read says so the card stands down rather than ask a person who just joined to join.
        joinCards={sessionStatus === 'authenticated' ? joinCards.filter((j) => j.state !== 'joining') : joinCards}
        featureCards={featureCards}
        composerLine={composerLine}
        guestInvite={guestInvite}
        composerDoor={composerDoor}
        crisisReferredAt={conv.crisisReferredAt}
        harmReferredAt={conv.harmReferredAt}
        crisisLines={conv.crisisLines}
        ageCheck={conv.ageCheck ? {
          onAnswered: (answer, reply) => {
            if (answer !== 'adult') { setNotServed(true); return; }
            setConv((c) => (c ? { ...c, ageCheck: null } : c));
            // Their answer as their line and Ember's reply to it, from the answer itself (a guest has no live channel).
            const turns = [reply.user_turn, reply.assistant_turn].filter((x): x is ConversationMessage => Boolean(x));
            if (turns.length) setMessages((m) => mergeTurns(m, turns));
          },
        } : null}
        history={{ hasMore: history.hasMore, loading: history.loading, onLoadOlder: () => { void loadOlder(); } }}
        onOpenEmber={openReading}
        arriving={arrival}
        helpAsk={helpAsk ? { paused: reading !== null, onClose: closeHelpAsk, onDone: () => setHelpAsk(null) } : null}
        viewer={{ avatarKey: viewerAvatar, onChangeAvatar: () => setPickingAvatar(true) }}
      />
      {pickingAvatar && (
        <AvatarSheet avatarKey={viewerAvatar} onSaved={setViewerAvatar} onClose={() => setPickingAvatar(false)} />
      )}
      {/* The page flies to the last of the batch — the one lowest in the
          thread, so bringing it into view brings the others with it. The
          layer stays mounted through the landing so its night can fade
          instead of cutting (it is opacity 0 and takes no pointer by then). */}
      {arrival && <EmberArrival emberIds={arrival.ids} onLanded={onArrivalLanded} />}
      {reading && <EmberReadPage ember={reading} owner={sessionStatus === 'authenticated'} note={emberNote} restored={readingRestored} onBack={() => openReading(null)} />}
      {/* The paper is already there while the thread is still loading it
          (2026-09-24): a Back from the light's page lands here, and without
          this the person sees the conversation for as long as the fetch takes
          before the Ember they pressed Back from returns. */}
      {!reading && wantEmber !== '' && <div className="ember-read is-restored" aria-hidden />}
    </TalkFrame>
  );
}
